// libs/core/shell/src/lib/theme/theme-applier.service.ts
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, AppConfig } from '@cardapio-online/config';
import { SystemConfigService } from '../system/system-config.service';
import type { ShellThemeMode } from '../system/system-config.interface';

@Injectable({ providedIn: 'root' })
export class ThemeApplierService {
  readonly storageKey = this.systemConfigService.storageKey;

  constructor(
    @Inject(APP_CONFIG) private readonly cfg: AppConfig,
    private readonly systemConfigService: SystemConfigService
  ) {}
  apply(root: HTMLElement = document.documentElement): void {
    /**
     * Regra:
     * - Se o BrandKernelService já aplicou --brand-primary, preservamos.
     * - Se ainda não aplicou, usamos APP_CONFIG como fallback inicial.
     *
     * Isso evita a regressão:
     * backend azul -> troca rota -> ThemeApplier volta para laranja.
     */
    const liveBrandPrimary = root.style
      .getPropertyValue('--brand-primary')
      .trim();

    const brandPrimary = liveBrandPrimary || this.cfg.brandPrimary;

    root.style.setProperty('--brand-primary', brandPrimary);
    root.style.setProperty('--shell-brand', brandPrimary);
    root.style.setProperty('--accent', brandPrimary);
    root.style.setProperty('--ds-color-primary', brandPrimary);

    const brandSecondary =
      'brandSecondary' in this.cfg
        ? String((this.cfg as { brandSecondary?: string }).brandSecondary ?? '')
        : '';

    if (brandSecondary) {
      root.style.setProperty('--brand-secondary', brandSecondary);
      root.style.setProperty('--shell-brand-2', brandSecondary);
      root.style.setProperty('--accent-2', brandSecondary);
    }

    const saved = this.systemConfigService.snapshot().themeMode;
    this.applyTheme(saved, root);
  }

  toggle(root: HTMLElement = document.documentElement): ShellThemeMode {
    const current =
      (root.getAttribute('data-theme') as ShellThemeMode | null) ?? 'dark';

    const next: ShellThemeMode = current === 'dark' ? 'light' : 'dark';
    this.set(next, root);
    return next;
  }

  set(theme: ShellThemeMode, root: HTMLElement = document.documentElement): void {
    this.applyTheme(theme, root);
    this.systemConfigService.update({ themeMode: theme });
  }

  getCurrent(root: HTMLElement = document.documentElement): ShellThemeMode {
    return (
      (root.getAttribute('data-theme') as ShellThemeMode | null) ??
      this.systemConfigService.snapshot().themeMode
    );
  }

  private applyTheme(
    theme: ShellThemeMode,
    root: HTMLElement = document.documentElement
  ): void {
    root.setAttribute('data-theme', theme);
  }
}
